/**
 * SlotPopover.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Popover contextual que aparece ao clicar em um slot vazio no calendário desktop.
 * Oferece ações: Novo agendamento, Bloquear horário, Reservar para paciente,
 * Sugerir disponibilidade.
 *
 * Posicionamento inteligente: ajusta para não sair da tela.
 * Fecha ao clicar fora (overlay transparente) ou pressionar Escape.
 * Animação de entrada: fadeIn + slideUp de 4px.
 *
 * @module SlotPopover
 */

import React, { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  WEEKDAYS_LONG,
  MONTHS,
} from '../../constants/calendarConstants';
import { copyToClipboard } from '../../utils/copyUtils';
import MessagesTemplate from '../../../../../consts/MessagesTemplate';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import CalendarConfig from '../../../../../consts/CalendarConfig';
import dayjs from 'dayjs';
import styles from './SlotPopover.module.css';
import AppointmentSlotPatientList from '../SlotOpportunities/AppointmentSlotPatientList';

/** Largura do popover em pixels */
const POPOVER_WIDTH = 360;

/** Altura estimada do popover em pixels (usada só antes da primeira medição real) */
const POPOVER_HEIGHT_ESTIMATE = 380;

/** Margem de segurança das bordas da tela */
const SCREEN_MARGIN = 12;

// ── Ícones SVG inline ─────────────────────────────────────────────────────────

const CalendarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const BanIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
  </svg>
);

const HourglassIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2h12M6 22h12"/>
    <path d="M7 2v4a5 5 0 0 0 10 0V2M7 22v-4a5 5 0 0 1 10 0v4"/>
  </svg>
);

const CopyIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

/**
 * Formata a data para o card de contexto do popover.
 * Exemplo: "26 Maio • Terça"
 *
 * @param {Date} date
 * @returns {string}
 */
function formatPopoverDate(date) {
  const day = date.getDate();
  const month = MONTHS[date.getMonth()];
  const weekday = WEEKDAYS_LONG[date.getDay()];
  return `${day} ${month} • ${weekday}`;
}

/**
 * Formata a hora para o card de contexto do popover.
 * Exemplo: "08:00"
 *
 * @param {number} hour
 * @param {number} minute
 * @returns {string}
 */
function formatPopoverTime(hour, minute) {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * Calcula a posição ajustada do popover para não sair da tela.
 *
 * A altura real varia bastante (lista de espera, indicadores), por isso ela
 * é medida no DOM depois de renderizar e passada aqui. Se não couber abaixo
 * do click, tenta acima; se não couber em nenhum dos dois, encosta na borda
 * inferior da tela, de modo que o popover inteiro fique sempre visível.
 *
 * @param {{ x: number, y: number }} clickPos - Posição do click
 * @param {number} [height] - Altura real do popover (fallback: estimativa)
 * @returns {{ left: number, top: number }}
 */
function calculatePosition(clickPos, height = POPOVER_HEIGHT_ESTIMATE) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let left = clickPos.x + 8;
  let top = clickPos.y + 8;

  // Ajusta horizontalmente se sair da tela
  if (left + POPOVER_WIDTH + SCREEN_MARGIN > vw) {
    left = clickPos.x - POPOVER_WIDTH - 8;
  }

  // Ajusta verticalmente se sair da tela
  if (top + height + SCREEN_MARGIN > vh) {
    const above = clickPos.y - height - 8;
    // Cabe acima do click? Senão, encosta na borda inferior
    top = above >= SCREEN_MARGIN ? above : vh - height - SCREEN_MARGIN;
  }

  // Garante que não saia pelo topo ou esquerda
  left = Math.max(SCREEN_MARGIN, left);
  top = Math.max(SCREEN_MARGIN, top);

  return { left, top };
}

/**
 * Popover contextual de slot vazio.
 *
 * @param {Object} props
 * @param {Object} props.slot - { date: Date, hour: number, minute: number }
 * @param {{ x: number, y: number }} props.position - Posição do click na tela
 * @param {boolean} [props.isAvailable] - Se o slot está disponível
 * @param {Function} props.onNewAppointment - Callback para novo agendamento
 * @param {Function} props.onBlockSlot - Callback para bloquear horário
 * @param {Function} [props.onReserveSlot] - Callback para reservar o horário para um paciente (link de cadastro)
 * @param {Function} props.onCopyDateTime - Callback para copiar data/hora
 * @param {Function} props.onClose - Callback para fechar
 * @returns {React.ReactElement}
 */
// ── Ícone de lista de espera ──────────────────────────────────────────────────
const WaitingListIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

/** Cache global para dados da lista de espera (evita chamadas repetidas) */
let _waitingListCache = null;
let _waitingListCacheTime = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

export default function SlotPopover({
  slot,
  position,
  isAvailable = true,
  onNewAppointment,
  onBlockSlot,
  onReserveSlot = null,
  onCopyDateTime,
  onClose,
  opportunity = null,
  onScheduleFromWaitingList = null,
  onNotifyPatient = null,
}) {
  const popoverRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [pos, setPos] = useState({ left: -9999, top: -9999 });
  const [waitingPatients, setWaitingPatients] = useState([]);

  const { CompanyConfig, GetWaitingList, patients: allPatients } = DataProviderInstance();

  // ── Load waiting list data and find compatible patients ───────────────────
  useEffect(() => {
    if (!slot) return;

    const slotDate = new Date(slot.date);
    const slotDateStr = `${slotDate.getFullYear()}-${String(slotDate.getMonth() + 1).padStart(2, '0')}-${String(slotDate.getDate()).padStart(2, '0')}`;
    const slotTimeStr = `${String(slot.hour).padStart(2, '0')}:${String(slot.minute).padStart(2, '0')}`;

    function findCompatible(items) {
      if (!items || !Array.isArray(items)) return [];
      return items.filter(item => {
        if (!item.availableDates || item.availableDates.length === 0) return false;
        if (item.status === 'SCHEDULED' || item.status === 'CANCELED') return false;
        return item.availableDates.some(ad => {
          const dt = ad.datetime || '';
          return dt.startsWith(slotDateStr) && dt.includes(slotTimeStr);
        });
      }).slice(0, 5); // Max 5
    }

    // Use cache if fresh
    if (_waitingListCache && (Date.now() - _waitingListCacheTime) < CACHE_TTL) {
      setWaitingPatients(findCompatible(_waitingListCache));
      return;
    }

    // Load from API
    GetWaitingList({ includeAvailability: true, daysLimit: 30 }, (response) => {
      if (response && response.success && response.data) {
        _waitingListCache = response.data;
        _waitingListCacheTime = Date.now();
        setWaitingPatients(findCompatible(response.data));
      }
    });
  }, [slot, GetWaitingList]);

  // Resolve patient names
  const waitingPatientNames = useMemo(() => {
    if (!waitingPatients.length || !allPatients) return [];
    return waitingPatients.map(wp => {
      const p = allPatients?.[wp.patientId + ''] || allPatients?.[wp.patientId];
      return p?.name?.split(' ')[0] || 'Paciente';
    });
  }, [waitingPatients, allPatients]);

  // Calcula posição com a altura real do popover, antes de pintar na tela.
  // Reexecuta quando o conteúdo muda (lista de espera carrega depois do mount).
  useLayoutEffect(() => {
    if (!position) return;
    const height = popoverRef.current?.offsetHeight || POPOVER_HEIGHT_ESTIMATE;
    setPos(calculatePosition(position, height));
  }, [position, opportunity, waitingPatients, copied]);

  // Reposiciona se o tamanho do popover ou da janela mudar
  useEffect(() => {
    if (!position || !popoverRef.current) return;

    const reposition = () => {
      const height = popoverRef.current?.offsetHeight || POPOVER_HEIGHT_ESTIMATE;
      setPos(calculatePosition(position, height));
    };

    window.addEventListener('resize', reposition);
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(reposition);
      observer.observe(popoverRef.current);
    }
    return () => {
      window.removeEventListener('resize', reposition);
      observer?.disconnect();
    };
  }, [position]);

  // Fecha ao pressionar Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Foca o popover ao abrir para acessibilidade
  useEffect(() => {
    if (popoverRef.current) {
      popoverRef.current.focus();
    }
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleNewAppointment = useCallback(() => {
    onNewAppointment?.();
    onClose?.();
  }, [onNewAppointment, onClose]);

  const handleBlockSlot = useCallback(() => {
    onBlockSlot?.();
    onClose?.();
  }, [onBlockSlot, onClose]);

  const handleReserveSlot = useCallback(() => {
    onReserveSlot?.();
    onClose?.();
  }, [onReserveSlot, onClose]);

  const handleCopy = useCallback(async () => {
    if (!slot?.date) return;

    // Cria Date com hora/minuto do slot
    const slotDate = new Date(slot.date);
    slotDate.setHours(slot.hour, slot.minute, 0, 0);

    // Formata a data no padrão "YYYY-MM-DD HH:mm:ss" para compatibilidade com CalendarConfig
    const dateStr = `${slotDate.getFullYear()}-${String(slotDate.getMonth() + 1).padStart(2, '0')}-${String(slotDate.getDate()).padStart(2, '0')} ${String(slot.hour).padStart(2, '0')}:${String(slot.minute).padStart(2, '0')}:00`;

    // Busca o template SuggestDate e substitui os placeholders
    const templates = MessagesTemplate.getTemplates(CompanyConfig);
    let message = templates.SuggestDate;

    if (message) {
      const yyyymmdd = CalendarConfig.GetAAAAMMDDArray(dateStr);
      const monthDay = yyyymmdd[2];
      const monthName = CalendarConfig.Months[yyyymmdd[1] - 1];
      const dayOfWeekIndex = dayjs(dateStr).day();

      message = message.replaceAll('{day}', monthDay);
      message = message.replaceAll('{month}', monthName);
      message = message.replaceAll('{time}', CalendarConfig.GetTime(dateStr));
      message = message.replaceAll('{when}', CalendarConfig.DaysOfWeek[dayOfWeekIndex]);
      // {name} não é substituído neste fluxo de cópia — limitação conhecida,
      // não há nome de cliente definido ainda nesta tela (fora de escopo consertar).
      message = message.replaceAll('{company_name}', CompanyConfig?.Name || '');

      message = MessagesTemplate.ConvertToClipboardText(message);
    } else {
      // Fallback caso o template não esteja disponível — texto aprovado
      // (docs/features/whatsapp-shared-number/revisao-mensagens-padrao.md, item 17).
      // {name} fica literal: mesma limitação conhecida do fluxo de cópia acima.
      const day = slotDate.getDate();
      const monthName = CalendarConfig.Months[slotDate.getMonth()];
      const weekday = CalendarConfig.DaysOfWeek[slotDate.getDay()];
      const time = `${String(slot.hour).padStart(2, '0')}:${String(slot.minute).padStart(2, '0')}`;
      const companyName = CompanyConfig?.Name || '';

      message =
        `Olá, {name}! Surgiu um horário disponível na *${companyName}*:\n\n` +
        `Data: ${weekday}, ${day} de ${monthName}, às ${time}\n\n` +
        `Tem interesse? Responda esta mensagem para reservar.`;

      message = MessagesTemplate.ConvertToClipboardText(message);
    }

    const result = await copyToClipboard(message);

    if (result.success) {
      setCopied(true);
      onCopyDateTime?.();
      setTimeout(() => setCopied(false), 2000);
    }
  }, [slot, onCopyDateTime, CompanyConfig]);

  if (!slot) return null;

  const dateLabel = formatPopoverDate(slot.date);
  const timeLabel = formatPopoverTime(slot.hour, slot.minute);

  // Texto de cópia para exibição no subtexto
  const copySubtext = (() => {
    const hours = String(slot.hour).padStart(2, '0');
    const minutes = String(slot.minute).padStart(2, '0');
    const d = new Date(slot.date);
    const day = d.getDate();
    const monthName = CalendarConfig.Months[d.getMonth()].toLowerCase();
    const weekday = CalendarConfig.DaysOfWeek[d.getDay()].toLowerCase();
    return `${hours}:${minutes} · ${day} de ${monthName} (${weekday})`;
  })();

  return (
    <>
      {/* Overlay transparente para fechar ao clicar fora */}
      <div
        className={styles.overlay}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Popover */}
      <div
        ref={popoverRef}
        className={styles.popover}
        style={{ left: pos.left, top: pos.top }}
        role="dialog"
        aria-modal="true"
        aria-label={`Opções para ${timeLabel} em ${dateLabel}`}
        tabIndex={-1}
      >

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className={styles.header}>
          <span className={styles.headerTitle}>HORÁRIO SELECIONADO</span>
          <button
            className={styles.closeButton}
            onClick={onClose}
            type="button"
            aria-label="Fechar"
          >
            <CloseIcon />
          </button>
        </div>

        {/* ── Card de contexto do horário ─────────────────────────────────── */}
        <div className={styles.contextCard}>
          <div className={styles.contextCardLeft}>
            <div className={styles.calendarIconWrapper}>
              <CalendarIcon />
            </div>
            <div className={styles.contextInfo}>
              <span className={styles.contextDate}>{dateLabel}</span>
              <span className={styles.contextTime}>{timeLabel}</span>
            </div>
          </div>
          <div className={styles.contextCardRight}>
            <span className={`${styles.statusBadge} ${isAvailable ? styles.statusAvailable : styles.statusOccupied}`}>
              {isAvailable ? (
                <><CheckIcon /> Disponível</>
              ) : (
                'Ocupado'
              )}
            </span>
          </div>
        </div>

        {/* ── Separador ──────────────────────────────────────────────────── */}
        <div className={styles.divider} />

        {/* ── Lista de espera — pacientes compatíveis com ações ─────────────── */}
        {opportunity && opportunity.patientCount > 0 && (
          <>
            <AppointmentSlotPatientList
              opportunity={opportunity}
              onSchedule={onScheduleFromWaitingList}
              onNotifyPatient={onNotifyPatient}
            />
            <div className={styles.divider} />
          </>
        )}

        {/* ── Indicador legado (fallback quando opportunity não foi passado) ── */}
        {!opportunity && waitingPatients.length > 0 && (
          <div
            className={styles.waitingListIndicator}
            title="Pacientes da lista de espera compatíveis com este horário"
          >
            <div className={styles.waitingListIcon}>
              <WaitingListIcon />
            </div>
            <div className={styles.waitingListInfo}>
              <span className={styles.waitingListTitle}>
                {waitingPatients.length} {waitingPatients.length === 1 ? 'paciente aguardando' : 'pacientes aguardando'}
              </span>
              <span className={styles.waitingListNames}>
                {waitingPatientNames.join(', ')}
              </span>
            </div>
          </div>
        )}

        {/* ── Texto de contexto ───────────────────────────────────────────── */}
        <p className={styles.contextQuestion}>O que deseja fazer?</p>

        {/* ── Action Cards ────────────────────────────────────────────────── */}
        <div className={styles.actionCards}>

          {/* Card Principal — Novo agendamento */}
          <button
            className={`${styles.actionCard} ${styles.actionCardPrimary}`}
            data-tour="agenda-slot-new"
            onClick={handleNewAppointment}
            type="button"
          >
            <div className={`${styles.actionIconWrapper} ${styles.actionIconPrimary}`}>
              <PlusIcon />
            </div>
            <div className={styles.actionCardContent}>
              <span className={`${styles.actionCardTitle} ${styles.actionCardTitlePrimary}`}>
                Novo agendamento
              </span>
              <span className={`${styles.actionCardSubtitle} ${styles.actionCardSubtitlePrimary}`}>
                Criar um novo agendamento neste horário
              </span>
            </div>
            <span className={`${styles.actionCardArrow} ${styles.actionCardArrowPrimary}`}>
              <ChevronRightIcon />
            </span>
          </button>

          {/* Card Secundário — Bloquear horário */}
          <button
            className={`${styles.actionCard} ${styles.actionCardSecondary}`}
            onClick={handleBlockSlot}
            type="button"
          >
            <div className={`${styles.actionIconWrapper} ${styles.actionIconSecondary}`}>
              <BanIcon />
            </div>
            <div className={styles.actionCardContent}>
              <span className={`${styles.actionCardTitle} ${styles.actionCardTitleSecondary}`}>
                Bloquear horário
              </span>
              <span className={styles.actionCardSubtitle}>
                Indisponibilizar este horário na agenda
              </span>
            </div>
            <span className={styles.actionCardArrow}>
              <ChevronRightIcon />
            </span>
          </button>

          {/* Card Secundário — Reservar para paciente (link de cadastro) */}
          {onReserveSlot && (
            <button
              className={`${styles.actionCard} ${styles.actionCardSecondary}`}
              onClick={handleReserveSlot}
              type="button"
            >
              <div className={`${styles.actionIconWrapper} ${styles.actionIconSecondary}`}>
                <HourglassIcon />
              </div>
              <div className={styles.actionCardContent}>
                <span className={`${styles.actionCardTitle} ${styles.actionCardTitleSecondary}`}>
                  Reservar para paciente
                </span>
                <span className={styles.actionCardSubtitle}>
                  Segurar o horário enquanto o paciente preenche o cadastro
                </span>
              </div>
              <span className={styles.actionCardArrow}>
                <ChevronRightIcon />
              </span>
            </button>
          )}

          {/* Card Terciário — Sugerir disponibilidade */}
          <button
            className={`${styles.actionCard} ${styles.actionCardTertiary} ${copied ? styles.actionCardCopied : ''}`}
            onClick={handleCopy}
            type="button"
            aria-live="polite"
          >
            <div className={`${styles.actionIconWrapper} ${styles.actionIconTertiary} ${copied ? styles.actionIconCopied : ''}`}>
              {copied ? <CheckIcon /> : <CopyIcon />}
            </div>
            <div className={styles.actionCardContent}>
              <span className={`${styles.actionCardTitle} ${styles.actionCardTitleTertiary}`}>
                {copied ? 'Copiado!' : 'Sugerir disponibilidade'}
              </span>
              <span className={styles.actionCardSubtitle}>
                {copied ? 'Copiado para a área de transferência' : copySubtext}
              </span>
            </div>
            <span className={styles.actionCardArrow}>
              <ChevronRightIcon />
            </span>
          </button>

        </div>
      </div>
    </>
  );
}
